import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle2,
  Clock,
  XCircle,
  UserCheck,
  CalendarDays,
  Download,
  Timer,
  Pencil,
  ChevronRight,
  ChevronDown,
  MapPin,
} from "lucide-react";

// Small pin that opens the coord in Google Maps in a new tab. Used next to
// each clock-in/out time so admins can audit where an employee tapped
// the button — required by the mandatory-location attendance flow.
const LocPin = ({ lat, lng, label }) => {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return (
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center text-[#0055FF] hover:text-[#0040CC] ml-1"
      title={label ? `${label} @ ${lat.toFixed(5)}, ${lng.toFixed(5)}` : "View on map"}
    >
      <MapPin className="w-3 h-3" />
    </a>
  );
};

const IST_TIME = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "—";
  }
};

const fmtHM = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

export default function Attendance() {
  const [pins, setPins] = useState({ employees: [], date: "", valid_until_ist: "" });
  const [roster, setRoster] = useState({ roster: [], date: "" });
  const [revealed, setRevealed] = useState({}); // {empId: true}
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState({});

  // Schedule editor modal
  const [editingEmp, setEditingEmp] = useState(null); // employee object
  const [editSched, setEditSched] = useState({
    checkin_start: "",
    on_time_cutoff: "",
    checkin_end: "",
    checkout_time: "",
  });
  const [savingSched, setSavingSched] = useState(false);

  // Per-employee monthly drill-down
  const [drillEmp, setDrillEmp] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});

  // History state
  const [tab, setTab] = useState("today");
  const [historyDays, setHistoryDays] = useState("30");
  const [historyView, setHistoryView] = useState("timesheet"); // "timesheet" | "grid"
  const [history, setHistory] = useState({ dates: [], employees: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Force-checkout dialog — admin picks an employee, an optional
  // HH:MM checkout time (defaults to now), and an optional reason. The
  // resulting attendance row is flagged and rendered in red.
  const [forceCoDlg, setForceCoDlg] = useState({
    open: false,
    employee: null,     // { id, name }
    time: "",           // HH:MM in 24h IST
    reason: "",
    submitting: false,
  });

  const openForceCheckout = (emp) => {
    // Prefill the time with the employee's scheduled checkout so the owner
    // just has to confirm (95% of forgotten clockouts happen at end-of-day).
    const sched = emp.schedule || {};
    setForceCoDlg({
      open: true,
      employee: { id: emp.id, name: emp.name },
      time: sched.checkout_time || "18:00",
      reason: "",
      submitting: false,
    });
  };

  const submitForceCheckout = async () => {
    if (!forceCoDlg.employee) return;
    setForceCoDlg((d) => ({ ...d, submitting: true }));
    try {
      await api.post("/attendance/force-checkout", {
        employee_id: forceCoDlg.employee.id,
        time: forceCoDlg.time.trim() || null,
        reason: forceCoDlg.reason.trim() || null,
      });
      toast.success(`Force-clocked-out ${forceCoDlg.employee.name}`);
      setForceCoDlg({ open: false, employee: null, time: "", reason: "", submitting: false });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not force checkout");
      setForceCoDlg((d) => ({ ...d, submitting: false }));
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/attendance/pins"),
        api.get("/attendance"),
      ]);
      setPins(p.data);
      setRoster(a.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 403) {
        // Interceptor will redirect if role really is wrong; otherwise
        // don't spam a "Forbidden" toast — show a friendly message.
        toast.error(
          "Only the workspace owner can see attendance. Please sign in as the owner."
        );
      } else {
        toast.error(detail || "Could not load attendance");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/attendance/history?days=${historyDays}`);
      setHistory(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    } finally {
      setLoadingHistory(false);
    }
  }, [historyDays]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  const exportCsv = () => {
    if (!history.employees.length) return;
    const rows = [];
    if (historyView === "timesheet") {
      rows.push([
        "Employee",
        "Email",
        "Department",
        "Date",
        "Check-in (IST)",
        "Check-out (IST)",
        "Overtime (H:MM)",
        "Late",
        "Auto-clocked-out",
      ]);
      history.employees.forEach((e) => {
        history.dates.forEach((d) => {
          const cell = e.days[d];
          if (!cell?.present) return;
          rows.push([
            e.name,
            e.email,
            e.department || "",
            d,
            IST_TIME(cell.in),
            cell.out ? IST_TIME(cell.out) : "—",
            cell.overtime_seconds > 0 ? fmtHM(cell.overtime_seconds) : "",
            cell.late ? "Yes" : "",
            cell.auto ? "Yes" : "",
          ]);
        });
      });
    } else {
      rows.push([
        "Employee",
        "Email",
        "Department",
        ...history.dates,
        "Present",
        "Absent",
      ]);
      history.employees.forEach((e) => {
        const cells = history.dates.map((d) => {
          const c = e.days[d];
          if (!c?.present) return "A";
          const t = IST_TIME(c.in);
          return c.late ? `LATE ${t}` : t;
        });
        rows.push([
          e.name,
          e.email,
          e.department || "",
          ...cells,
          e.present_count,
          e.absent_count,
        ]);
      });
    }
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${history.dates[0] || ""}_to_${history.dates.slice(-1)[0] || ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDrillCsv = useCallback(() => {
    if (!drillData) return;
    const emp = drillData.employee;
    const sched = drillData.schedule || {};

    // Top-of-file metadata rows.
    const meta = [
      ["Employee", emp?.name || ""],
      ["Email", emp?.email || ""],
      ["Department", emp?.department || ""],
      ["Phone", emp?.phone || ""],
      [
        "Schedule (IST)",
        `in ${sched.checkin_start} · on-time by ${sched.on_time_cutoff} · out ${sched.checkout_time} · end ${sched.checkin_end}`,
      ],
      ["Window from", drillData.window_from || ""],
      ["Total present", drillData.totals?.present ?? ""],
      ["Total absent", drillData.totals?.absent ?? ""],
      ["Total late", drillData.totals?.late ?? ""],
      ["Total overtime", fmtHM(drillData.totals?.overtime_seconds || 0)],
      [],
    ];

    const dailyHeader = [
      "Month",
      "Date",
      "Day",
      "Check-in (IST)",
      "Check-out (IST)",
      "Overtime (H:MM)",
      "Late",
      "Auto-out",
      "Status",
    ];

    const rows = [];
    (drillData.months || []).forEach((m) => {
      (m.days || []).forEach((d) => {
        const dt = new Date(d.date);
        if (!d.present) {
          rows.push([
            m.label,
            d.date,
            dt.toLocaleDateString("en-IN", { weekday: "short" }),
            "",
            "",
            "",
            "",
            "",
            "Absent",
          ]);
          return;
        }
        rows.push([
          m.label,
          d.date,
          dt.toLocaleDateString("en-IN", { weekday: "short" }),
          IST_TIME(d.in),
          d.out ? IST_TIME(d.out) : "—",
          d.overtime_seconds > 0 ? fmtHM(d.overtime_seconds) : "",
          d.is_late ? "Yes" : "",
          d.auto ? "Yes" : "",
          "Present",
        ]);
      });
    });

    // If we ever want month totals appended, keep it simple: append a "Monthly summary" table.
    const summary = [
      [],
      ["Monthly summary"],
      [
        "Month",
        "Working days",
        "Present",
        "Absent",
        "Late",
        "Overtime (H:MM)",
        "Avg check-in (IST)",
      ],
      ...(drillData.months || []).map((m) => [
        m.label,
        m.working_days,
        m.present,
        m.absent,
        m.late,
        m.overtime_seconds > 0 ? fmtHM(m.overtime_seconds) : "",
        m.avg_checkin_ist || "",
      ]),
    ];

    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const allRows = [...meta, dailyHeader, ...rows, ...summary];
    const csv = allRows.map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = (emp?.name || "employee").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `attendance-${safeName}-${stamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  }, [drillData]);

  const openDrill = useCallback(async (emp) => {
    setDrillEmp(emp);
    setDrillData(null);
    setExpandedMonths({});
    setDrillLoading(true);
    try {
      const { data } = await api.get(
        `/attendance/history/${emp.id}/monthly?months=6`
      );
      setDrillData(data);
      // auto-expand the current (first) month for quick access.
      if (data?.months?.length) {
        setExpandedMonths({ [data.months[0].month]: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    } finally {
      setDrillLoading(false);
    }
  }, []);

  const openScheduleEditor = (emp) => {
    const s = emp.schedule || {
      checkin_start: "09:00",
      on_time_cutoff: "10:00",
      checkin_end: "20:30",
      checkout_time: "18:00",
    };
    setEditSched({
      checkin_start: s.checkin_start,
      on_time_cutoff: s.on_time_cutoff,
      checkin_end: s.checkin_end,
      checkout_time: s.checkout_time,
    });
    setEditingEmp(emp);
  };

  const saveSchedule = async () => {
    if (!editingEmp) return;
    // basic client-side validation
    const { checkin_start, on_time_cutoff, checkin_end } = editSched;
    if (!(checkin_start <= on_time_cutoff && on_time_cutoff <= checkin_end)) {
      toast.error("Check-in start ≤ On-time cutoff ≤ Check-in end.");
      return;
    }
    setSavingSched(true);
    try {
      const { data } = await api.put(
        `/attendance/schedules/${editingEmp.id}`,
        editSched
      );
      setPins((prev) => ({
        ...prev,
        employees: prev.employees.map((e) =>
          e.id === editingEmp.id ? { ...e, schedule: data.schedule } : e
        ),
      }));
      toast.success("Schedule updated");
      setEditingEmp(null);
      // reload roster so late / overtime reflect the new times.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally {
      setSavingSched(false);
    }
  };

  const rotate = async (empId) => {
    setRotating((r) => ({ ...r, [empId]: true }));
    try {
      const { data } = await api.post(`/attendance/pins/${empId}/rotate`);
      setPins((prev) => ({
        ...prev,
        employees: prev.employees.map((e) =>
          e.id === empId ? { ...e, pin: data.pin } : e
        ),
      }));
      setRevealed((r) => ({ ...r, [empId]: true }));
      toast.success("New PIN generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rotate PIN");
    } finally {
      setRotating((r) => ({ ...r, [empId]: false }));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      /* ignore */
    }
  };

  const rosterById = Object.fromEntries((roster.roster || []).map((r) => [r.id, r]));
  const presentCount = (roster.roster || []).filter((r) => r.present).length;
  const totalCount = (roster.roster || []).length;

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-attendance">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Daily attendance
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">Who&apos;s clocked in?</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Each employee has a private 6-digit PIN today. Share it privately —
            they enter it in the mobile app to mark themselves present.
            Every employee follows their <span className="text-slate-800 font-medium">own schedule</span>{" "}
            — click the pencil next to their timings to change it.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Today ({pins.date || roster.date})
            </div>
            <div className="font-heading text-2xl font-bold mt-0.5">
              <span data-testid="attendance-present-count" className="text-emerald-600">
                {presentCount}
              </span>
              <span className="text-slate-400"> / {totalCount}</span>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={load}
            data-testid="attendance-refresh"
            className="border-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="today" data-testid="attendance-tab-today">
            <UserCheck className="w-4 h-4 mr-1" /> Today
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="attendance-tab-history">
            <CalendarDays className="w-4 h-4 mr-1" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-4">
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b flex-row items-center justify-between space-y-0">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-[#0055FF]" />
            Team roster
          </CardTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
            Auto-out per employee schedule
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Loading attendance…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Employee</th>
                  <th className="text-left px-5 py-3">Team</th>
                  <th className="text-left px-5 py-3">Today&apos;s PIN</th>
                  <th className="text-left px-5 py-3">Schedule (IST)</th>
                  <th className="text-left px-5 py-3">Check-in</th>
                  <th className="text-left px-5 py-3">Check-out</th>
                  <th className="text-left px-5 py-3">Overtime</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pins.employees.map((e) => {
                  const r = rosterById[e.id];
                  const isRevealed = revealed[e.id];
                  return (
                    <tr key={e.id} data-testid={`attendance-row-${e.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#0055FF] text-white grid place-items-center font-heading font-bold text-xs">
                            {e.name.slice(0, 1)}
                          </div>
                          <div>
                            <div className="font-medium">{e.name}</div>
                            <div className="mono text-[10px] text-slate-500">
                              {e.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="mono text-[10px] uppercase px-2 py-1 rounded bg-slate-100 text-slate-700">
                          {e.department}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`mono text-lg tracking-widest font-bold ${
                              isRevealed ? "text-slate-900" : "text-slate-400"
                            }`}
                            data-testid={`attendance-pin-${e.id}`}
                          >
                            {isRevealed ? e.pin : "••••••"}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setRevealed((r) => ({ ...r, [e.id]: !r[e.id] }))
                            }
                            className="text-slate-400 hover:text-slate-700 p-1"
                            aria-label="toggle pin"
                            data-testid={`attendance-toggle-${e.id}`}
                          >
                            {isRevealed ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => copy(e.pin)}
                            className="text-slate-400 hover:text-[#0055FF] p-1"
                            aria-label="copy pin"
                            data-testid={`attendance-copy-${e.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => rotate(e.id)}
                            disabled={rotating[e.id]}
                            className="text-slate-400 hover:text-[#FF5000] p-1 disabled:opacity-50"
                            aria-label="rotate pin"
                            data-testid={`attendance-rotate-${e.id}`}
                          >
                            <RefreshCw
                              className={`w-4 h-4 ${
                                rotating[e.id] ? "animate-spin" : ""
                              }`}
                            />
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3 mono text-[11px] text-slate-700">
                        <div className="flex items-center gap-2">
                          <div className="leading-tight">
                            <div>
                              <span className="text-slate-400">in </span>
                              {e.schedule?.checkin_start || "09:00"}
                            </div>
                            <div>
                              <span className="text-slate-400">late&gt; </span>
                              <span className="text-red-600">
                                {e.schedule?.on_time_cutoff || "10:00"}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400">out </span>
                              {e.schedule?.checkout_time || "18:00"}
                              <span className="text-slate-400"> · end </span>
                              {e.schedule?.checkin_end || "20:30"}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => openScheduleEditor(e)}
                            className="text-slate-400 hover:text-[#0055FF] p-1 self-start"
                            aria-label="edit schedule"
                            data-testid={`attendance-editsched-${e.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3 mono text-xs text-slate-700">
                        {r?.checked_in_at ? (
                          <div className="flex items-center gap-1.5">
                            <span
                              className={r?.is_late ? "text-red-600 font-bold" : ""}
                            >
                              {IST_TIME(r.checked_in_at)}
                            </span>
                            {r?.is_late && (
                              <span
                                className="mono text-[9px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                data-testid={`attendance-late-${e.id}`}
                              >
                                late
                              </span>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-5 py-3 mono text-xs">
                        {r?.checked_out_at ? (
                          <span
                            className={`flex items-center gap-1 ${
                              r.force_clocked_out ? "text-red-700 font-semibold" : "text-slate-700"
                            }`}
                            data-testid={`attendance-checkout-${e.id}`}
                          >
                            {IST_TIME(r.checked_out_at)}
                            {r.checked_out_auto && (
                              <span className="mono text-[9px] uppercase text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                                auto
                              </span>
                            )}
                            {r.force_clocked_out && (
                              <span
                                className="mono text-[9px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                title={r.force_reason ? `Reason: ${r.force_reason}` : `Force-closed by ${r.force_clocked_out_by_name || "owner"}`}
                                data-testid={`attendance-force-badge-${e.id}`}
                              >
                                forced
                              </span>
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-5 py-3 mono text-xs">
                        {r?.overtime_seconds > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                            data-testid={`attendance-overtime-${e.id}`}
                          >
                            <Timer className="w-3 h-3" />
                            {fmtHM(r.overtime_seconds)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {!r?.present && (
                          <span className="mono text-[10px] uppercase text-slate-500 bg-slate-100 rounded px-2 py-1 flex items-center gap-1 w-fit">
                            <XCircle className="w-3 h-3" /> absent
                          </span>
                        )}
                        {r?.present && !r?.checked_out_at && (
                          <div className="flex items-center gap-2">
                            <span className="mono text-[10px] uppercase text-emerald-700 bg-emerald-50 rounded px-2 py-1 flex items-center gap-1 w-fit">
                              <CheckCircle2 className="w-3 h-3" /> in
                            </span>
                            <button
                              type="button"
                              onClick={() => openForceCheckout(e)}
                              className="mono text-[10px] uppercase text-red-700 border border-red-300 hover:bg-red-50 rounded px-2 py-1"
                              title="Employee forgot to clock out — force close their shift"
                              data-testid={`attendance-force-checkout-${e.id}`}
                            >
                              Force out
                            </button>
                          </div>
                        )}
                        {r?.present && r?.checked_out_at && (
                          <span
                            className={`mono text-[10px] uppercase rounded px-2 py-1 flex items-center gap-1 w-fit ${
                              r.force_clocked_out
                                ? "text-red-700 bg-red-50 border border-red-200"
                                : "text-slate-600 bg-slate-100"
                            }`}
                          >
                            <Clock className="w-3 h-3" /> out
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {pins.employees.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center px-5 py-10 text-slate-500 text-sm"
                    >
                      Add employees first, then their PINs will show up here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="border-slate-200 shadow-none">
            <CardHeader className="border-b flex-row items-center justify-between space-y-0 flex-wrap gap-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#0055FF]" />
                Attendance history
              </CardTitle>
              <div className="flex items-center gap-2">
                <div
                  className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50"
                  data-testid="attendance-history-view"
                >
                  <button
                    type="button"
                    onClick={() => setHistoryView("timesheet")}
                    className={
                      "px-2.5 h-7 rounded mono text-[10px] uppercase tracking-widest " +
                      (historyView === "timesheet"
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800")
                    }
                    data-testid="attendance-history-view-timesheet"
                  >
                    Timesheet
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryView("grid")}
                    className={
                      "px-2.5 h-7 rounded mono text-[10px] uppercase tracking-widest " +
                      (historyView === "grid"
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800")
                    }
                    data-testid="attendance-history-view-grid"
                  >
                    Grid
                  </button>
                </div>
                <Select value={historyDays} onValueChange={setHistoryDays}>
                  <SelectTrigger
                    className="h-8 w-40"
                    data-testid="attendance-history-range"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCsv}
                  disabled={!history.employees.length}
                  data-testid="attendance-history-export"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {loadingHistory ? (
                <div className="p-10 text-center text-sm text-slate-500">
                  Loading history…
                </div>
              ) : history.employees.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">
                  No employee attendance yet in this range.
                </div>
              ) : historyView === "timesheet" ? (
                (() => {
                  // Flatten present-day cells into a sortable list of rows.
                  const flat = [];
                  history.employees.forEach((e) => {
                    history.dates.forEach((d) => {
                      const cell = e.days[d];
                      if (cell?.present) flat.push({ emp: e, date: d, cell });
                    });
                  });
                  // newest first
                  flat.sort((a, b) => (a.date < b.date ? 1 : -1));
                  if (flat.length === 0) {
                    return (
                      <div className="p-10 text-center text-sm text-slate-500">
                        No check-ins recorded in this range.
                      </div>
                    );
                  }
                  return (
                    <table
                      className="w-full text-sm"
                      data-testid="attendance-history-timesheet"
                    >
                      <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="text-left px-4 py-3">Employee</th>
                          <th className="text-left px-4 py-3">Team</th>
                          <th className="text-left px-4 py-3">Date</th>
                          <th className="text-left px-4 py-3">Check-in</th>
                          <th className="text-left px-4 py-3">Check-out</th>
                          <th className="text-left px-4 py-3">Overtime</th>
                          <th className="text-left px-4 py-3">Flags</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {flat.map(({ emp, date, cell }) => {
                          const dt = new Date(date);
                          const dateLabel = `${dt.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                          })} · ${dt.toLocaleDateString("en-IN", {
                            weekday: "short",
                          })}`;
                          return (
                            <tr
                              key={`${emp.id}-${date}`}
                              data-testid={`att-ts-${emp.id}-${date}`}
                            >
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => openDrill(emp)}
                                  className="text-left group"
                                  data-testid={`att-history-drill-${emp.id}`}
                                >
                                  <div className="font-medium text-sm group-hover:text-[#0055FF] group-hover:underline">
                                    {emp.name}
                                  </div>
                                  <div className="mono text-[10px] text-slate-500">
                                    {emp.email}
                                  </div>
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <span className="mono text-[10px] uppercase px-2 py-1 rounded bg-slate-100 text-slate-700">
                                  {emp.department || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-3 mono text-xs text-slate-700">
                                {dateLabel}
                              </td>
                              <td className="px-4 py-3 mono text-xs">
                                <span
                                  className={
                                    cell.late
                                      ? "text-red-600 font-bold"
                                      : "text-slate-800"
                                  }
                                >
                                  {IST_TIME(cell.in)}
                                </span>
                              </td>
                              <td className="px-4 py-3 mono text-xs">
                                {cell.out ? (
                                  <span className="text-slate-800">
                                    {IST_TIME(cell.out)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 mono text-xs">
                                {cell.overtime_seconds > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                                    <Timer className="w-3 h-3" />
                                    {fmtHM(cell.overtime_seconds)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {cell.late && (
                                    <span className="mono text-[9px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                      late
                                    </span>
                                  )}
                                  {cell.auto && (
                                    <span className="mono text-[9px] uppercase text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                                      auto-out
                                    </span>
                                  )}
                                  {!cell.late && !cell.auto && (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 min-w-[180px]">
                        Employee
                      </th>
                      {history.dates.map((d) => {
                        const dt = new Date(d);
                        return (
                          <th
                            key={d}
                            className="px-2 py-3 text-center whitespace-nowrap"
                            title={d}
                          >
                            <div className="text-[9px]">
                              {dt.toLocaleDateString("en-IN", {
                                month: "short",
                              })}
                            </div>
                            <div className="text-[11px] text-slate-800">
                              {dt.getDate()}
                            </div>
                          </th>
                        );
                      })}
                      <th className="px-3 py-3 text-center text-emerald-600">P</th>
                      <th className="px-3 py-3 text-center text-red-500">A</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {history.employees.map((e) => (
                      <tr
                        key={e.id}
                        data-testid={`attendance-history-row-${e.id}`}
                      >
                        <td className="sticky left-0 z-10 bg-white px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openDrill(e)}
                            className="text-left group"
                            data-testid={`att-history-drill-grid-${e.id}`}
                          >
                            <div className="font-medium text-sm group-hover:text-[#0055FF] group-hover:underline">
                              {e.name}
                            </div>
                            <div className="mono text-[10px] text-slate-500">
                              {e.department}
                            </div>
                          </button>
                        </td>
                        {history.dates.map((d) => {
                          const cell = e.days[d];
                          const present = cell?.present;
                          const inTime = cell?.in ? IST_TIME(cell.in) : null;
                          const outTime = cell?.out ? IST_TIME(cell.out) : null;
                          return (
                            <td
                              key={d}
                              className="px-2 py-3 text-center whitespace-nowrap"
                              title={
                                present
                                  ? `In: ${inTime}${cell.out ? ` · Out: ${outTime}` : ""}${cell.auto ? " (auto)" : ""}${cell.late ? " · LATE" : ""}${cell.overtime_seconds > 0 ? ` · OT ${fmtHM(cell.overtime_seconds)}` : ""}`
                                  : "Absent"
                              }
                              data-testid={`att-cell-${e.id}-${d}`}
                            >
                              {present ? (
                                <div
                                  className={
                                    "inline-flex flex-col items-center rounded-md px-1.5 py-1 mono text-[10px] leading-tight " +
                                    (cell.late
                                      ? "bg-red-50 border border-red-200 text-red-700"
                                      : "bg-emerald-50 border border-emerald-200 text-emerald-800")
                                  }
                                >
                                  <span className="font-bold">{inTime}</span>
                                  <span className="text-[9px] opacity-70">
                                    {outTime || "—"}
                                  </span>
                                  {cell.overtime_seconds > 0 && (
                                    <span className="text-[9px] text-emerald-700 font-bold">
                                      +{fmtHM(cell.overtime_seconds)}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md mono text-[10px] font-bold bg-slate-100 text-slate-400">
                                  ·
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center mono text-sm text-emerald-700 font-bold">
                          {e.present_count}
                        </td>
                        <td className="px-3 py-3 text-center mono text-sm text-slate-500">
                          {e.absent_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 mono text-[9px] font-bold">
                ✓
              </span>{" "}
              On-time
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-red-50 border border-red-200 text-red-700 mono text-[9px] font-bold">
                !
              </span>{" "}
              Late
            </div>
            <div className="flex items-center gap-1">
              <span className="mono text-[10px] text-emerald-700 font-bold">
                +H:MM
              </span>{" "}
              Overtime past scheduled check-out
            </div>
            <div className="mono text-[10px]">
              Grid cells show <b>check-in</b> above <b>check-out</b>. Timesheet
              view lists every present day.
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!drillEmp}
        onOpenChange={(o) => {
          if (!o) {
            setDrillEmp(null);
            setDrillData(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="drill-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              {drillEmp?.name} — Monthly attendance
            </DialogTitle>
            <DialogDescription>
              Last 6 months. Present / absent counts, late days, total overtime
              and average clock-in time in IST.
            </DialogDescription>
          </DialogHeader>
          {drillLoading && (
            <div className="p-8 text-center text-sm text-slate-500">
              Loading…
            </div>
          )}
          {drillData && !drillLoading && (
            <div className="space-y-4">
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportDrillCsv}
                  data-testid="drill-export-csv"
                  className="border-slate-200"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-emerald-700">
                    Present
                  </div>
                  <div
                    className="text-2xl font-heading text-emerald-800"
                    data-testid="drill-total-present"
                  >
                    {drillData.totals.present}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                    Absent
                  </div>
                  <div
                    className="text-2xl font-heading text-slate-800"
                    data-testid="drill-total-absent"
                  >
                    {drillData.totals.absent}
                  </div>
                </div>
                <div className="rounded-md border border-red-200 bg-red-50/50 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-red-700">
                    Late
                  </div>
                  <div
                    className="text-2xl font-heading text-red-700"
                    data-testid="drill-total-late"
                  >
                    {drillData.totals.late}
                  </div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-emerald-700">
                    Overtime
                  </div>
                  <div
                    className="text-2xl font-heading text-emerald-800"
                    data-testid="drill-total-overtime"
                  >
                    {fmtHM(drillData.totals.overtime_seconds)}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-slate-200 overflow-hidden">
                <table
                  className="w-full text-sm"
                  data-testid="drill-monthly-table"
                >
                  <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Month</th>
                      <th className="text-right px-3 py-2">Working days</th>
                      <th className="text-right px-3 py-2 text-emerald-700">
                        Present
                      </th>
                      <th className="text-right px-3 py-2">Absent</th>
                      <th className="text-right px-3 py-2 text-red-700">
                        Late
                      </th>
                      <th className="text-right px-3 py-2 text-emerald-700">
                        Overtime
                      </th>
                      <th className="text-right px-3 py-2">Avg check-in</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {drillData.months.map((m) => {
                      const expanded = !!expandedMonths[m.month];
                      const days = m.days || [];
                      const clickable = days.length > 0;
                      return (
                        <React.Fragment key={m.month}>
                          <tr
                            data-testid={`drill-row-${m.month}`}
                            className={
                              clickable
                                ? "cursor-pointer hover:bg-slate-50"
                                : ""
                            }
                            onClick={() => {
                              if (!clickable) return;
                              setExpandedMonths((prev) => ({
                                ...prev,
                                [m.month]: !prev[m.month],
                              }));
                            }}
                          >
                            <td className="px-3 py-2 text-slate-400">
                              {clickable ? (
                                expanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )
                              ) : null}
                            </td>
                            <td className="px-3 py-2 font-medium">{m.label}</td>
                            <td className="px-3 py-2 text-right mono text-xs text-slate-600">
                              {m.working_days}
                            </td>
                            <td className="px-3 py-2 text-right mono text-xs text-emerald-800 font-bold">
                              {m.present}
                            </td>
                            <td className="px-3 py-2 text-right mono text-xs text-slate-500">
                              {m.absent}
                            </td>
                            <td className="px-3 py-2 text-right mono text-xs text-red-700">
                              {m.late || "—"}
                            </td>
                            <td className="px-3 py-2 text-right mono text-xs">
                              {m.overtime_seconds > 0 ? (
                                <span className="text-emerald-700 font-bold">
                                  {fmtHM(m.overtime_seconds)}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right mono text-xs text-slate-700">
                              {m.avg_checkin_ist || "—"}
                            </td>
                          </tr>
                          {expanded && clickable && (
                            <tr
                              data-testid={`drill-days-${m.month}`}
                            >
                              <td colSpan={8} className="bg-slate-50/60 p-0">
                                <table className="w-full text-xs">
                                  <thead className="mono text-[9px] uppercase tracking-widest text-slate-500">
                                    <tr>
                                      <th className="text-left px-6 py-2">
                                        Date
                                      </th>
                                      <th className="text-left px-3 py-2">
                                        Check-in
                                      </th>
                                      <th className="text-left px-3 py-2">
                                        Check-out
                                      </th>
                                      <th className="text-left px-3 py-2">
                                        Overtime
                                      </th>
                                      <th className="text-left px-3 py-2">
                                        Flags
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {days.map((d) => {
                                      const dt = new Date(d.date);
                                      const label = `${dt.toLocaleDateString(
                                        "en-IN",
                                        {
                                          day: "2-digit",
                                          month: "short",
                                        }
                                      )} · ${dt.toLocaleDateString("en-IN", {
                                        weekday: "short",
                                      })}`;
                                      if (!d.present) {
                                        return (
                                          <tr
                                            key={d.date}
                                            className="bg-slate-50/40"
                                            data-testid={`drill-day-${m.month}-${d.date}`}
                                          >
                                            <td className="px-6 py-1.5 mono text-[11px] text-slate-400">
                                              {label}
                                            </td>
                                            <td
                                              colSpan={3}
                                              className="px-3 py-1.5 mono text-[11px] text-slate-400"
                                            >
                                              —
                                            </td>
                                            <td className="px-3 py-1.5">
                                              <span
                                                className="mono text-[9px] uppercase text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                                                data-testid={`drill-absent-${m.month}-${d.date}`}
                                              >
                                                absent
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      }
                                      return (
                                        <tr
                                          key={d.date}
                                          data-testid={`drill-day-${m.month}-${d.date}`}
                                        >
                                          <td className="px-6 py-1.5 mono text-[11px] text-slate-700">
                                            {label}
                                          </td>
                                          <td className="px-3 py-1.5 mono text-[11px]">
                                            <span
                                              className={
                                                d.is_late
                                                  ? "text-red-600 font-bold"
                                                  : "text-slate-800"
                                              }
                                            >
                                              {IST_TIME(d.in)}
                                            </span>
                                            <LocPin lat={d.in_lat} lng={d.in_lng} label="Clock-in" />
                                          </td>
                                          <td className="px-3 py-1.5 mono text-[11px] text-slate-700">
                                            {d.out ? (
                                              <>
                                                {IST_TIME(d.out)}
                                                <LocPin lat={d.out_lat} lng={d.out_lng} label="Clock-out" />
                                              </>
                                            ) : (
                                              <span className="text-slate-400">
                                                —
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-1.5 mono text-[11px]">
                                            {d.overtime_seconds > 0 ? (
                                              <span className="text-emerald-700 font-bold">
                                                {fmtHM(d.overtime_seconds)}
                                              </span>
                                            ) : (
                                              <span className="text-slate-400">
                                                —
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-1.5">
                                            <div className="flex gap-1 flex-wrap">
                                              {d.is_late && (
                                                <span className="mono text-[9px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                                  late
                                                </span>
                                              )}
                                              {d.auto && (
                                                <span className="mono text-[9px] uppercase text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                                                  auto-out
                                                </span>
                                              )}
                                              {!d.is_late && !d.auto && (
                                                <span
                                                  className="mono text-[9px] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                                                  data-testid={`drill-present-${m.month}-${d.date}`}
                                                >
                                                  present
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mono text-[10px] text-slate-500">
                Current schedule (IST):{" "}
                <span className="text-slate-700">
                  in {drillData.schedule.checkin_start} · on-time by{" "}
                  <span className="text-red-600">
                    {drillData.schedule.on_time_cutoff}
                  </span>{" "}
                  · out{" "}
                  <span className="text-emerald-700">
                    {drillData.schedule.checkout_time}
                  </span>{" "}
                  · window ends {drillData.schedule.checkin_end}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingEmp} onOpenChange={(o) => !o && setEditingEmp(null)}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Schedule · {editingEmp?.name}
            </DialogTitle>
            <DialogDescription>
              All times are IST (24-hour). Employees who clock in after the
              on-time cutoff are marked <span className="text-red-600 font-semibold">late</span>.
              Time worked past the check-out time counts as <span className="text-emerald-700 font-semibold">overtime</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <label className="space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Check-in start
              </div>
              <Input
                type="time"
                value={editSched.checkin_start}
                onChange={(e) =>
                  setEditSched((s) => ({ ...s, checkin_start: e.target.value }))
                }
                data-testid="sched-checkin-start"
              />
            </label>
            <label className="space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-red-600">
                On-time cutoff
              </div>
              <Input
                type="time"
                value={editSched.on_time_cutoff}
                onChange={(e) =>
                  setEditSched((s) => ({ ...s, on_time_cutoff: e.target.value }))
                }
                data-testid="sched-on-time"
              />
            </label>
            <label className="space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-700">
                Check-out (scheduled)
              </div>
              <Input
                type="time"
                value={editSched.checkout_time}
                onChange={(e) =>
                  setEditSched((s) => ({ ...s, checkout_time: e.target.value }))
                }
                data-testid="sched-checkout"
              />
            </label>
            <label className="space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Check-in window ends
              </div>
              <Input
                type="time"
                value={editSched.checkin_end}
                onChange={(e) =>
                  setEditSched((s) => ({ ...s, checkin_end: e.target.value }))
                }
                data-testid="sched-checkin-end"
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingEmp(null)}
              disabled={savingSched}
            >
              Cancel
            </Button>
            <Button
              onClick={saveSchedule}
              disabled={savingSched}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="sched-save"
            >
              {savingSched ? "Saving…" : "Save schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force-checkout dialog — opens from the "Force out" pill on any
          currently-clocked-in row. Owner picks a time (defaults to the
          employee's scheduled checkout) and an optional reason. */}
      <Dialog
        open={forceCoDlg.open}
        onOpenChange={(v) => setForceCoDlg((d) => ({ ...d, open: v }))}
      >
        <DialogContent data-testid="force-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-red-600" />
              Force clock-out — {forceCoDlg.employee?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              The employee forgot to clock out. Set the actual end-of-shift time
              below. The record will show up in <b className="text-red-700">red</b> with a
              &quot;forced&quot; badge so the audit trail is transparent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Checkout time (IST)</label>
              <Input
                type="time"
                value={forceCoDlg.time}
                onChange={(e) => setForceCoDlg((d) => ({ ...d, time: e.target.value }))}
                data-testid="force-checkout-time"
              />
              <p className="mono text-[10px] text-slate-500">
                Format: HH:MM 24-hour. Leave blank to use the current time.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Reason (optional)</label>
              <Input
                value={forceCoDlg.reason}
                onChange={(e) => setForceCoDlg((d) => ({ ...d, reason: e.target.value }))}
                placeholder="e.g. Forgot to clock out — closed by supervisor"
                data-testid="force-checkout-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setForceCoDlg({ open: false, employee: null, time: "", reason: "", submitting: false })}
              data-testid="force-checkout-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitForceCheckout}
              disabled={forceCoDlg.submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="force-checkout-submit"
            >
              {forceCoDlg.submitting ? "Closing shift..." : "Force clock-out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
